import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaTimes, FaPlus, FaEdit, FaTrash, FaSave } from 'react-icons/fa';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import styles from './CustomerAddressModal.module.css';
import { useTheme } from '../../contexts/ThemeContext';

const CustomerAddressModal = ({
  isOpen,
  onClose,
  selectedCustomer,
}) => {
  const { isDarkMode } = useTheme();
  const [addresses, setAddresses] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [newAddress, setNewAddress] = useState({
    title: '',
    address: '',
    city: '',
    district: '',
    postalCode: '',
    isDefault: false,
    notes: ''
  });

  // Adresleri yükle
  const fetchAddresses = async () => {
    if (!selectedCustomer) return;
    
    try {
      const addressesRef = collection(db, 'CustomerAddresses');
      const q = query(
        addressesRef, 
        where('customerId', '==', selectedCustomer.id)
      );
      const querySnapshot = await getDocs(q);
      const addressesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Client-side sorting
      addressesData.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          const aTime = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const bTime = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return bTime - aTime;
        }
        return 0;
      });
      
      setAddresses(addressesData);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      toast.error('Adresler yüklenirken hata oluştu');
    }
  };

  useEffect(() => {
    if (isOpen && selectedCustomer) {
      fetchAddresses();
    }
  }, [isOpen, selectedCustomer]);

  // Yeni adres ekle
  const handleAddAddress = async (e) => {
    e.preventDefault();
    
    if (!newAddress.title.trim() || !newAddress.address.trim()) {
      toast.error('Başlık ve adres alanları zorunludur');
      return;
    }

    try {
      const addressData = {
        ...newAddress,
        customerId: selectedCustomer.id,
        customerName: selectedCustomer['Başlık'] || selectedCustomer['Müşteri Adı'] || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Eğer varsayılan adres olarak işaretleniyorsa, diğer adresleri varsayılan olmaktan çıkar
      if (newAddress.isDefault) {
        const defaultAddresses = addresses.filter(addr => addr.isDefault);
        for (const addr of defaultAddresses) {
          await updateDoc(doc(db, 'CustomerAddresses', addr.id), { isDefault: false });
        }
      }

      await addDoc(collection(db, 'CustomerAddresses'), addressData);
      toast.success('Adres başarıyla eklendi');
      setNewAddress({
        title: '',
        address: '',
        city: '',
        district: '',
        postalCode: '',
        isDefault: false,
        notes: ''
      });
      setIsAddModalOpen(false);
      fetchAddresses();
    } catch (error) {
      console.error('Error adding address:', error);
      toast.error('Adres eklenirken hata oluştu');
    }
  };

  // Adres düzenle
  const handleEditAddress = async (e) => {
    e.preventDefault();
    
    if (!editingAddress.title.trim() || !editingAddress.address.trim()) {
      toast.error('Başlık ve adres alanları zorunludur');
      return;
    }

    try {
      const addressData = {
        ...editingAddress,
        updatedAt: serverTimestamp()
      };

      // Eğer varsayılan adres olarak işaretleniyorsa, diğer adresleri varsayılan olmaktan çıkar
      if (editingAddress.isDefault) {
        const defaultAddresses = addresses.filter(addr => addr.isDefault && addr.id !== editingAddress.id);
        for (const addr of defaultAddresses) {
          await updateDoc(doc(db, 'CustomerAddresses', addr.id), { isDefault: false });
        }
      }

      await updateDoc(doc(db, 'CustomerAddresses', editingAddress.id), addressData);
      toast.success('Adres başarıyla güncellendi');
      setEditingAddress(null);
      setIsEditModalOpen(false);
      fetchAddresses();
    } catch (error) {
      console.error('Error updating address:', error);
      toast.error('Adres güncellenirken hata oluştu');
    }
  };

  // Adres sil
  const handleDeleteAddress = async (addressId) => {
    if (window.confirm('Bu adresi silmek istediğinizden emin misiniz?')) {
      try {
        await deleteDoc(doc(db, 'CustomerAddresses', addressId));
        toast.success('Adres başarıyla silindi');
        fetchAddresses();
      } catch (error) {
        console.error('Error deleting address:', error);
        toast.error('Adres silinirken hata oluştu');
      }
    }
  };

  // Düzenleme modalını aç
  const openEditModal = (address) => {
    setEditingAddress({ ...address });
    setIsEditModalOpen(true);
  };

  // Modal kapatma
  const closeModal = () => {
    onClose();
    setAddresses([]);
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setEditingAddress(null);
  };

  // Overlay tıklama
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaMapMarkerAlt className={styles.modalIcon} />
            {`Adresler - ${selectedCustomer?.['Başlık'] || selectedCustomer?.['Müşteri Adı'] || ''}`}
          </h2>
          <button 
            className={styles.closeButton} 
            onClick={closeModal}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <div className={styles.headerActions}>
            <button 
              className={styles.addButton}
              onClick={() => setIsAddModalOpen(true)}
            >
              <FaPlus className={styles.buttonIcon} />
              Yeni Adres Ekle
            </button>
          </div>

          <div className={styles.addressesList}>
            {addresses.length === 0 ? (
              <div className={styles.noAddresses}>
                <FaMapMarkerAlt className={styles.noAddressesIcon} />
                <p>Henüz adres eklenmemiş</p>
              </div>
            ) : (
              addresses.map((address) => (
                <div key={address.id} className={styles.addressCard}>
                  <div className={styles.addressHeader}>
                    <h3 className={styles.addressTitle}>
                      {address.title}
                      {address.isDefault && (
                        <span className={styles.defaultBadge}>Varsayılan</span>
                      )}
                    </h3>
                    <div className={styles.addressActions}>
                      <button 
                        className={styles.editButton}
                        onClick={() => openEditModal(address)}
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className={styles.deleteButton}
                        onClick={() => handleDeleteAddress(address.id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <div className={styles.addressDetails}>
                    <p className={styles.addressText}>{address.address}</p>
                    <div className={styles.addressInfo}>
                      <span>{address.district}, {address.city}</span>
                      {address.postalCode && <span> - {address.postalCode}</span>}
                    </div>
                    {address.notes && (
                      <p className={styles.addressNotes}>{address.notes}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Yeni Adres Ekleme Modal */}
      {isAddModalOpen && (
        <div className={styles.subModalOverlay} onClick={(e) => e.target === e.currentTarget && setIsAddModalOpen(false)}>
          <div className={styles.subModalContent}>
            <div className={styles.subModalHeader}>
              <h3>Yeni Adres Ekle</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setIsAddModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddAddress} className={styles.addressForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>İl</label>
                  <input
                    type="text"
                    value={newAddress.city}
                    onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                    placeholder="İl"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>İlçe</label>
                  <input
                    type="text"
                    value={newAddress.district}
                    onChange={(e) => setNewAddress({...newAddress, district: e.target.value})}
                    placeholder="İlçe"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Adres *</label>
                <textarea
                  value={newAddress.address}
                  onChange={(e) => setNewAddress({...newAddress, address: e.target.value})}
                  required
                  rows="3"
                  placeholder="Tam adres bilgisi"
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Başlık *</label>
                  <input
                    type="text"
                    value={newAddress.title}
                    onChange={(e) => setNewAddress({...newAddress, title: e.target.value})}
                    required
                    placeholder="Örn: Merkez Ofis, Fabrika"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Posta Kodu</label>
                  <input
                    type="text"
                    value={newAddress.postalCode}
                    onChange={(e) => setNewAddress({...newAddress, postalCode: e.target.value})}
                    placeholder="Posta kodu"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>
                  <input
                    type="checkbox"
                    checked={newAddress.isDefault}
                    onChange={(e) => setNewAddress({...newAddress, isDefault: e.target.checked})}
                  />
                  Varsayılan Adres
                </label>
              </div>

              <div className={styles.formGroup}>
                <label>Notlar</label>
                <textarea
                  value={newAddress.notes}
                  onChange={(e) => setNewAddress({...newAddress, notes: e.target.value})}
                  rows="2"
                  placeholder="Ek notlar"
                />
              </div>

              <div className={styles.formActions}>
                <button type="button" onClick={() => setIsAddModalOpen(false)}>
                  İptal
                </button>
                <button type="submit">
                  <FaSave className={styles.buttonIcon} />
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adres Düzenleme Modal */}
      {isEditModalOpen && editingAddress && (
        <div className={styles.subModalOverlay} onClick={(e) => e.target === e.currentTarget && setIsEditModalOpen(false)}>
          <div className={styles.subModalContent}>
            <div className={styles.subModalHeader}>
              <h3>Adres Düzenle</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setIsEditModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleEditAddress} className={styles.addressForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>İl</label>
                  <input
                    type="text"
                    value={editingAddress.city}
                    onChange={(e) => setEditingAddress({...editingAddress, city: e.target.value})}
                    placeholder="İl"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>İlçe</label>
                  <input
                    type="text"
                    value={editingAddress.district}
                    onChange={(e) => setEditingAddress({...editingAddress, district: e.target.value})}
                    placeholder="İlçe"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Adres *</label>
                <textarea
                  value={editingAddress.address}
                  onChange={(e) => setEditingAddress({...editingAddress, address: e.target.value})}
                  required
                  rows="3"
                  placeholder="Tam adres bilgisi"
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Başlık *</label>
                  <input
                    type="text"
                    value={editingAddress.title}
                    onChange={(e) => setEditingAddress({...editingAddress, title: e.target.value})}
                    required
                    placeholder="Örn: Merkez Ofis, Fabrika"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Posta Kodu</label>
                  <input
                    type="text"
                    value={editingAddress.postalCode}
                    onChange={(e) => setEditingAddress({...editingAddress, postalCode: e.target.value})}
                    placeholder="Posta kodu"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>
                  <input
                    type="checkbox"
                    checked={editingAddress.isDefault}
                    onChange={(e) => setEditingAddress({...editingAddress, isDefault: e.target.checked})}
                  />
                  Varsayılan Adres
                </label>
              </div>

              <div className={styles.formGroup}>
                <label>Notlar</label>
                <textarea
                  value={editingAddress.notes}
                  onChange={(e) => setEditingAddress({...editingAddress, notes: e.target.value})}
                  rows="2"
                  placeholder="Ek notlar"
                />
              </div>

              <div className={styles.formActions}>
                <button type="button" onClick={() => setIsEditModalOpen(false)}>
                  İptal
                </button>
                <button type="submit">
                  <FaSave className={styles.buttonIcon} />
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAddressModal;
